import {
  AssignQRCodeRequestDTO,
  AssignQRCodeResponseDTO,
  AwaMigrationStatusResponseDTO,
  CreateInspectionRequestDTO,
  CreateInspectionResponseDTO,
  CreateIssueRequestDTO,
  CreateIssueResponseDTO,
  CreateRepairRequestDTO,
  CreateRepairResponseDTO,
  CreateScheduleFormRequestDTO,
  CreateScheduleFormResponseDTO,
  FinishAwaMigrationRequestDTO,
  FinishAwaMigrationResponseDTO,
  GenerateDocumentPresignedUploadUrlRequestDTO,
  GenerateDocumentPresignedUploadUrlResponseDTO,
  GenerateIssueFilePresignedUploadUrlRequestDTO,
  GenerateIssueFilePresignedUploadUrlResponseDTO,
  GeneratePdfAttachmentFormFieldPresignedUploadUrlRequestDTO,
  GeneratePdfAttachmentFormFieldPresignedUploadUrlResponseDTO,
  GetActionsRequestDTO,
  GetActionsResponseDTO,
  GetActivitiesByAssetRequestDTO,
  GetActivitiesByAssetResponseDTO,
  GetAssetByIdResponseDTO,
  GetAssetFormsByTypeRequestDTO,
  GetAssetFormsByTypeResponseDTO,
  GetAssetsRequestDTO,
  GetAssetsResponseDTO,
  GetCategoriesRequestDTO,
  GetCategoriesResponseDTO,
  GetCheckRequestDTO,
  GetCheckResponseDTO,
  GetChecksByAssetRequestDTO,
  GetChecksByAssetResponseDTO,
  GetContactsRequestDTO,
  GetContactsResponseDTO,
  GetDocumentsByAssetRequestDTO,
  GetDocumentsByAssetResponseDTO,
  GetFormByIdResponseDTO,
  GetFormRevisionByIdRequestDTO,
  GetFormRevisionByIdResponseDTO,
  GetFormSubmissionRevisionsResponseDTO,
  GetInspectionByIdRequestDTO,
  GetInspectionByIdResponseDTO,
  GetInspectionRevisionsRequestDTO,
  GetInspectionsRequestDTO,
  GetInspectionsResponseDTO,
  GetIssueByIdResponseDTO,
  GetIssueChecksByAssetRequestDTO,
  GetIssueChecksByAssetResponseDTO,
  GetIssuesByAssetRequestDTO,
  GetIssuesByAssetResponseDTO,
  GetIssuesRequestDTO,
  GetIssuesResponseDTO,
  GetLatestInspectionByAssetRequestDTO,
  GetLatestInspectionByAssetResponseDTO,
  GetListByIdResponseDTO,
  GetLocationsRequestDTO,
  GetLocationsResponseDTO,
  GetPublicWorkspaceByIdResponseDTO,
  GetQRCodeByValueRequestDTO,
  GetQRCodeByValueResponseDTO,
  GetRepairByIdRequestDTO,
  GetRepairByIdResponseDTO,
  GetRepairRevisionsRequestDTO,
  GetScheduleByIdRequestDTO,
  GetScheduleByIdResponseDTO,
  GetScheduleFormByIdRequestDTO,
  GetScheduleFormByIdResponseDTO,
  GetScheduleFormRevisionsRequestDTO,
  GetSitemateUserResponseDTO,
  GetTagsRequestDTO,
  GetTagsResponseDTO,
  GetUnitsRequestDTO,
  GetUnitsResponseDTO,
  GetWorkOrdersRequestDTO,
  GetWorkOrdersResponseDTO,
  InitiateAwaMigrationRequestDTO,
  InitiateAwaMigrationResponseDTO,
  SendInspectionToEmailRequestDTO,
  SendInspectionToEmailResponseDTO,
  UpdateInspectionRequestDTO,
  UpdateInspectionResponseDTO,
  UpdateRepairRequestDTO,
  UpdateRepairResponseDTO,
  UpdateScheduleFormRequestDTO,
  UpdateScheduleFormResponseDTO,
  VerifyAwaMigrationOtpRequestDTO,
  VerifyAwaMigrationOtpResponseDTO,
} from '@gearbelt/global';
import ky from 'ky';
import qs from 'qs';

import { env } from '@/env';
import { useAWAStore } from '@/stores/awa.store';
import { isTokenValid } from '@/utils/token';

import { AWAGlobalApiService } from './global-api/awa-global-api.service';

export abstract class AWAApiV2Service {
  static client = ky.create({
    prefixUrl: `${env.VITE_API_URL}/api/v2/`,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 20000,
    retry: 0,
    hooks: {
      beforeRequest: [
        async (request) => {
          const { workspace, authTokens, setAuthTokens } = useAWAStore.getState();

          if (workspace?.id) {
            request.headers.set('x-workspace-id', workspace.id);
          }

          if (authTokens?.accessToken) {
            const valid = isTokenValid(authTokens);

            request.headers.set('authtype', 'fusionauth');

            if (!valid) {
              try {
                const result = await AWAGlobalApiService.refreshAccessToken({
                  accessToken: authTokens.accessToken,
                  refreshToken: authTokens.refreshToken,
                });

                setAuthTokens({
                  accessToken: result.accessToken,
                  accessTokenExpiration: result.accessTokenExpiration ? new Date(result.accessTokenExpiration).getTime() : undefined,
                  refreshToken: result.refreshToken,
                  refreshTokenId: result.refreshTokenId,
                });

                request.headers.set('Authorization', `AWA ${result.accessToken}`);
              } catch {
                setAuthTokens(null);
                throw new Error('Failed to refresh access token');
              }
            } else {
              request.headers.set('Authorization', `AWA ${authTokens.accessToken}`);
            }
          }
        },
      ],
    },
  });

  static async GetAssets(dto: GetAssetsRequestDTO) {
    return await this.client.get<GetAssetsResponseDTO>('assets', {
      searchParams: this.serializeParams(dto),
    });
  }

  static async GetAssetById(assetId: string) {
    return await this.client.get<GetAssetByIdResponseDTO>(`assets/${assetId}`);
  }

  static async GetDocumentsByAsset(dto: GetDocumentsByAssetRequestDTO) {
    return await this.client.get<GetDocumentsByAssetResponseDTO>(`documents/asset/${dto.assetId}`, {
      searchParams: this.serializeParams(dto),
    });
  }

  static async GetAssetFormsByType({ assetId, type, limit }: GetAssetFormsByTypeRequestDTO) {
    return await this.client.get<GetAssetFormsByTypeResponseDTO>(`asset-forms/assets/${assetId}/type/${type}`, {
      searchParams: limit ? { limit: limit.toString() } : undefined,
    });
  }

  static async GetListById(listId: string) {
    return await this.client.get<GetListByIdResponseDTO>(`lists/${listId}`);
  }

  static async GetSitemateUser() {
    return await this.client.get<GetSitemateUserResponseDTO>('users/current-sitemate-user', {});
  }

  static async CreateIssue(dto: CreateIssueRequestDTO) {
    return await this.client.post<CreateIssueResponseDTO>('issues', {
      json: dto,
    });
  }

  static async InitiateAwaMigration(dto: InitiateAwaMigrationRequestDTO) {
    return await this.client.post<InitiateAwaMigrationResponseDTO>('auth/awa/migrate/initiate', {
      json: dto,
    });
  }

  static async VerifyAwaMigrationOtp(dto: VerifyAwaMigrationOtpRequestDTO) {
    return await this.client.post<VerifyAwaMigrationOtpResponseDTO>('auth/awa/migrate/verify-otp', {
      json: dto,
    });
  }

  static async FinishAwaMigration(dto: FinishAwaMigrationRequestDTO) {
    return await this.client.post<FinishAwaMigrationResponseDTO>('auth/awa/migrate/finish', {
      json: dto,
    });
  }

  static async GetAwaMigrationStatus() {
    return await this.client.get<AwaMigrationStatusResponseDTO>('auth/awa/migrate/status', {});
  }

  static async GetIssues(dto: GetIssuesRequestDTO) {
    return await this.client.get<GetIssuesResponseDTO>('issues', {
      searchParams: this.serializeParams(dto),
    });
  }

  static async GetIssuesByAsset(dto: GetIssuesByAssetRequestDTO) {
    return await this.client.get<GetIssuesByAssetResponseDTO>(`issues/assets/${dto.assetId}`, {
      searchParams: this.serializeParams(dto),
    });
  }

  static async GetLocations(dto: GetLocationsRequestDTO) {
    return await this.client.get<GetLocationsResponseDTO>('locations', {
      searchParams: this.serializeParams(dto),
    });
  }

  static async GetContacts(dto: GetContactsRequestDTO) {
    return await this.client.get<GetContactsResponseDTO>('contacts', {
      searchParams: this.serializeParams(dto),
    });
  }

  static async CreateInspectionForm(dto: CreateInspectionRequestDTO) {
    return await this.client.post<CreateInspectionResponseDTO>('inspections', {
      json: dto,
    });
  }

  static async CreateRepairForm(dto: CreateRepairRequestDTO) {
    return await this.client.post<CreateRepairResponseDTO>('repairs', {
      json: dto,
    });
  }

  static async UpdateInspection(inspectionId: string, dto: UpdateInspectionRequestDTO) {
    return await this.client.patch<UpdateInspectionResponseDTO>(`inspections/${inspectionId}`, {
      json: dto,
    });
  }

  static async UpdateRepair(repairId: string, dto: UpdateRepairRequestDTO) {
    return await this.client.patch<UpdateRepairResponseDTO>(`repairs/${repairId}`, {
      json: dto,
    });
  }

  static async UpdateScheduleForm(scheduleFormId: string, dto: UpdateScheduleFormRequestDTO) {
    return await this.client.patch<UpdateScheduleFormResponseDTO>(`schedule-forms/${scheduleFormId}`, {
      json: dto,
    });
  }

  static async GetRepairById(dto: GetRepairByIdRequestDTO) {
    return await this.client.get<GetRepairByIdResponseDTO>(`repairs/${dto.repairId}`);
  }

  static async CreateScheduleFormSubmission(dto: CreateScheduleFormRequestDTO) {
    return await this.client.post<CreateScheduleFormResponseDTO>('schedule-forms', {
      json: dto,
    });
  }

  static async GetScheduleFormById(dto: GetScheduleFormByIdRequestDTO) {
    return await this.client.get<GetScheduleFormByIdResponseDTO>(`schedule-forms/${dto.scheduleFormId}`);
  }

  static async GetLatestInspectionByAsset(dto: GetLatestInspectionByAssetRequestDTO) {
    return await this.client.get<GetLatestInspectionByAssetResponseDTO>(`inspections/assets/${dto.assetId}/latest`);
  }

  static async SendInspectionToEmail(dto: SendInspectionToEmailRequestDTO) {
    const { inspectionId, ...body } = dto;
    return await this.client.post<SendInspectionToEmailResponseDTO>(`inspections/${inspectionId}/send-email`, {
      json: body,
    });
  }

  static async GetIssueChecksByAsset(dto: GetIssueChecksByAssetRequestDTO) {
    return await this.client.get<GetIssueChecksByAssetResponseDTO>(`issues/assets/${dto.assetId}/checks`);
  }

  static async GetActivityByAsset(dto: GetActivitiesByAssetRequestDTO) {
    return await this.client.get<GetActivitiesByAssetResponseDTO>(`activities/assets/${dto.assetId}`);
  }

  static async GetInspections(dto: GetInspectionsRequestDTO) {
    return await this.client.get<GetInspectionsResponseDTO>('inspections', {
      searchParams: this.serializeParams(dto),
    });
  }

  static async GetInspectionById(dto: GetInspectionByIdRequestDTO) {
    return await this.client.get<GetInspectionByIdResponseDTO>(`inspections/${dto.inspectionId}`);
  }

  static async GetInspectionRevisions(dto: GetInspectionRevisionsRequestDTO) {
    return await this.client.get<GetFormSubmissionRevisionsResponseDTO>(`inspections/${dto.inspectionId}/revisions`);
  }

  static async GetRepairRevisions(dto: GetRepairRevisionsRequestDTO) {
    return await this.client.get<GetFormSubmissionRevisionsResponseDTO>(`repairs/${dto.repairId}/revisions`);
  }

  static async GetScheduleFormRevisions(dto: GetScheduleFormRevisionsRequestDTO) {
    return await this.client.get<GetFormSubmissionRevisionsResponseDTO>(`schedule-forms/${dto.scheduleFormId}/revisions`);
  }

  static async GetFormById(formId: string) {
    return await this.client.get<GetFormByIdResponseDTO>(`forms/${formId}`);
  }

  static async GetFormRevisionById(dto: GetFormRevisionByIdRequestDTO) {
    return await this.client.get<GetFormRevisionByIdResponseDTO>(`forms/revisions/${dto.formRevisionId}`);
  }

  static async GetQRCodeByValue(dto: GetQRCodeByValueRequestDTO) {
    const searchParams = dto.track === false ? { track: 'false' } : undefined;
    return await this.client.get<GetQRCodeByValueResponseDTO>(`qr-codes/value/${dto.value}`, {
      searchParams,
    });
  }

  static async AssignQRCode(dto: AssignQRCodeRequestDTO) {
    return await this.client.post<AssignQRCodeResponseDTO>('qr-codes/assign', {
      json: dto,
    });
  }

  static async GetPublicWorkspaceById(workspaceId: string) {
    return await this.client.get<GetPublicWorkspaceByIdResponseDTO>(`workspaces/${workspaceId}/public`);
  }

  static async GetTags(dto: GetTagsRequestDTO) {
    return await this.client.get<GetTagsResponseDTO>('tags', {
      searchParams: this.serializeParams(dto),
    });
  }

  static async GetCategories(dto: GetCategoriesRequestDTO) {
    return await this.client.get<GetCategoriesResponseDTO>('categories', {
      searchParams: this.serializeParams(dto),
    });
  }

  static async GetIssueById(issueId: string) {
    return await this.client.get<GetIssueByIdResponseDTO>(`issues/${issueId}`);
  }

  static async GetActions(dto: GetActionsRequestDTO) {
    return await this.client.get<GetActionsResponseDTO>('actions', {
      searchParams: this.serializeParams(dto),
    });
  }

  static async GetUnits(dto: GetUnitsRequestDTO) {
    return await this.client.get<GetUnitsResponseDTO>('units', {
      searchParams: this.serializeParams(dto),
    });
  }

  static async GetWorkOrders(dto: GetWorkOrdersRequestDTO) {
    return await this.client.get<GetWorkOrdersResponseDTO>('work-orders', {
      searchParams: this.serializeParams(dto),
    });
  }

  static async GenerateDocumentPresignedUploadUrl(dto: GenerateDocumentPresignedUploadUrlRequestDTO) {
    return await this.client.post<GenerateDocumentPresignedUploadUrlResponseDTO>('file/generate-document-presigned-upload-url', {
      json: dto,
    });
  }

  static async GenerateIssueFilePresignedUploadUrl(dto: GenerateIssueFilePresignedUploadUrlRequestDTO) {
    return await this.client.post<GenerateIssueFilePresignedUploadUrlResponseDTO>('file/generate-issue-file-presigned-upload-url', {
      json: dto,
    });
  }

  static async GeneratePdfAttachmentFormFieldPresignedUploadUrl(dto: GeneratePdfAttachmentFormFieldPresignedUploadUrlRequestDTO) {
    return await this.client.post<GeneratePdfAttachmentFormFieldPresignedUploadUrlResponseDTO>('file/generate-pdf-attachment-form-field-presigned-upload-url', {
      json: dto,
    });
  }

  static async GetCheckById(dto: GetCheckRequestDTO) {
    return await this.client.get<GetCheckResponseDTO>(`checks/${dto.checkId}`);
  }

  static async GetChecksByAsset(dto: GetChecksByAssetRequestDTO) {
    return await this.client.get<GetChecksByAssetResponseDTO>(`checks/assets/${dto.assetId}`);
  }

  static async GetScheduleById(dto: GetScheduleByIdRequestDTO) {
    return await this.client.get<GetScheduleByIdResponseDTO>(`schedules/${dto.scheduleId}`);
  }

  private static serializeParams<T>(params: T): string {
    return qs.stringify(params);
  }
}
